import { Box } from '@mui/material';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@/primitives';
import {
  EBV_CATEGORY,
  EBV_TRAIT_COLUMN_HEADER_HEIGHT,
  categoryCellSx,
} from '@/hooks/publicSearch/ebv/ebvColumns';
import BreedAverageEbvCell from '@/components/publicSearch/animal/ebv/BreedAverageEbvCell';
import { EbvTraitHeaderLabel } from '@/components/publicSearch/animal/ebv/EbvTraitHeaderLabel';
import { coerceBreedAverageRaw } from '@/components/publicSearch/animal/ebv/breedAverageDisplay';
import type { EbvTraitHeaderContext } from '@/components/publicSearch/animal/ebv/ebvTraitExplanations';
import type { EbvAverageData, SelectionIndexColumn } from './animalEbvTabTypes';

type AnimalEbvTabSelectionIndexTableProps = {
  isMobile: boolean;
  selectionIndexColumns: SelectionIndexColumn[];
  showPercentileColumn: boolean;
  ebvAverageData: EbvAverageData | null;
  currencySymbol: string;
  traitHeaderContext: EbvTraitHeaderContext;
};

export default function AnimalEbvTabSelectionIndexTable({
  isMobile,
  selectionIndexColumns,
  showPercentileColumn,
  ebvAverageData,
  currencySymbol,
  traitHeaderContext,
}: AnimalEbvTabSelectionIndexTableProps) {
  return (
    <Box>
      <Typography sx={{ fontWeight: 700, mb: 1, textAlign: 'center' }}>
        SELECTION INDEX VALUES
      </Typography>

      <Box
        sx={{
          backgroundColor: 'background.paper',
          border: 1,
          borderColor: 'divider',
          borderRadius: 2,
          overflowX: 'auto',
        }}
      >
        <Table
          size="small"
          sx={{
            minWidth: { xs: 'auto', md: 900 },
            width: isMobile ? '100%' : undefined,
            tableLayout: 'fixed',
            '& td:not(:first-of-type)': { whiteSpace: 'normal' },
            '& td:first-of-type': { whiteSpace: 'normal' },
            '& th': {
              whiteSpace: 'normal',
              lineHeight: 1.2,
              verticalAlign: 'bottom',
              minHeight: EBV_TRAIT_COLUMN_HEADER_HEIGHT,
              px: 0.75,
              py: 0.5,
            },
          }}
        >
          <colgroup>
            {/* Phones give the market-target gutter only what it needs and let
                the index columns share the rest, so the table stops scrolling
                sideways (ILRX-4700). */}
            <col style={{ width: isMobile ? 86 : 120 }} />
            {selectionIndexColumns.map((indexCol) =>
              isMobile ? (
                <col key={indexCol.key} />
              ) : (
                <col key={indexCol.key} style={{ width: 140 }} />
              ),
            )}
          </colgroup>
          <TableHead>
            <TableRow>
              <TableCell>
                <Typography
                  variant="bodysmall"
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 600,
                  }}
                >
                  Market Target
                </Typography>
              </TableCell>
              {selectionIndexColumns.map((indexCol, i) => (
                <TableCell
                  key={indexCol.key}
                  align="center"
                  sx={categoryCellSx(EBV_CATEGORY.INDEXES, {
                    left: i === 0,
                    right: i === selectionIndexColumns.length - 1,
                    top: true,
                    header: true,
                  })}
                >
                  <EbvTraitHeaderLabel
                    label={isMobile ? indexCol.shortLabel : indexCol.label}
                    traitKey={indexCol.traitKey}
                    traitHeaderContext={traitHeaderContext}
                  />
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow hover>
              <TableCell>
                <Typography
                  variant="bodysmall"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  Index Value
                </Typography>
              </TableCell>
              {selectionIndexColumns.map((indexCol, i) => (
                <TableCell
                  key={indexCol.key}
                  align="center"
                  sx={categoryCellSx(EBV_CATEGORY.INDEXES, {
                    left: i === 0,
                    right: i === selectionIndexColumns.length - 1,
                  })}
                >
                  {indexCol.ebvValue || '—'}
                </TableCell>
              ))}
            </TableRow>

            <TableRow hover>
              <TableCell>
                <Typography
                  variant="bodysmall"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  Breed average
                </Typography>
              </TableCell>
              {selectionIndexColumns.map((indexCol, i) => (
                <TableCell
                  key={indexCol.key}
                  align="center"
                  sx={categoryCellSx(EBV_CATEGORY.INDEXES, {
                    left: i === 0,
                    right: i === selectionIndexColumns.length - 1,
                  })}
                >
                  {coerceBreedAverageRaw(ebvAverageData?.[indexCol.avgKey]) ? (
                    <BreedAverageEbvCell
                      raw={coerceBreedAverageRaw(
                        ebvAverageData?.[indexCol.avgKey],
                      )}
                      format={indexCol.format}
                      currencySymbol={currencySymbol}
                    />
                  ) : (
                    '—'
                  )}
                </TableCell>
              ))}
            </TableRow>

            {showPercentileColumn && (
              <TableRow hover>
                <TableCell>
                  <Typography
                    variant="bodysmall"
                    sx={{
                      color: 'text.secondary',
                    }}
                  >
                    Percentile
                  </Typography>
                </TableCell>
                {selectionIndexColumns.map((indexCol, i) => (
                  <TableCell
                    key={indexCol.key}
                    align="center"
                    sx={categoryCellSx(EBV_CATEGORY.INDEXES, {
                      left: i === 0,
                      right: i === selectionIndexColumns.length - 1,
                    })}
                  >
                    {indexCol.percentile || '—'}
                  </TableCell>
                ))}
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
}
